import { useId, useState, type FormEvent } from "react";
import type { InteriorThemeUpdateResponse, StoreThemeId } from "@townsy/contracts";
import { putInteriorTheme } from "../api/endpoints";
import { Dialog } from "./Dialog";
import {
  STORE_THEME_OPTIONS,
  STORE_THEME_PICKER_INTRO,
  STORE_THEME_THUMB_HEIGHT,
  STORE_THEME_THUMB_WIDTH,
  storeThemeLabel,
  storeThemeSwatch,
  storeThemeThumbnailAlt,
  storeThemeThumbnailUrl,
  themeSaveErrorMessage,
} from "./storeThemeLogic";

/**
 * Store interior picker (ADR-0006 #5, ADR-0008 #4): the 15 presets with a preview thumbnail, their
 * description and the current one marked. One radio group, so the arrow keys move between presets
 * and screen readers announce "N of 15". Choosing is free; the server checks ownership, audits the
 * change and tells everyone inside through `property.changed`, so visitors see the room change
 * without leaving it.
 */

/** A preview image with reserved dimensions; a missing or broken image falls back to the preset's colours. */
export function StoreThemeThumbnailView({ themeId, failed, onError }: { themeId: StoreThemeId; failed: boolean; onError?: () => void }) {
  const option = STORE_THEME_OPTIONS.find((o) => o.id === themeId);
  const label = option?.label ?? themeId;
  if (failed) {
    const sw = storeThemeSwatch(themeId);
    return (
      <span
        className="theme-thumb theme-thumb-fallback"
        role="img"
        aria-label={`${label} interior (no preview image)`}
        data-testid={`store-theme-thumb-fallback-${themeId}`}
        style={{ background: `linear-gradient(to bottom, ${sw.wall} 0 58%, ${sw.floor} 58% 100%)` }}
      >
        <span className="theme-thumb-fallback-label" aria-hidden="true">
          {label}
        </span>
      </span>
    );
  }
  return (
    <img
      className="theme-thumb"
      src={storeThemeThumbnailUrl(themeId)}
      width={STORE_THEME_THUMB_WIDTH}
      height={STORE_THEME_THUMB_HEIGHT}
      alt={storeThemeThumbnailAlt({ label })}
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={onError}
      data-testid={`store-theme-thumb-${themeId}`}
    />
  );
}

export function StoreThemeThumbnail({ themeId }: { themeId: StoreThemeId }) {
  const [failedId, setFailedId] = useState<StoreThemeId | null>(null);
  return <StoreThemeThumbnailView themeId={themeId} failed={failedId === themeId} onError={() => setFailedId(themeId)} />;
}

export interface StoreThemePickerViewProps {
  businessName: string;
  current: StoreThemeId;
  selected: StoreThemeId;
  pending: boolean;
  error: string | null;
  onSelect(theme: StoreThemeId): void;
  onApply(): void;
  onClose(): void;
  /** Themes whose preview failed to load (server-rendered tests pass them in; the live dialog tracks them). */
  failedThumbnails?: ReadonlySet<StoreThemeId>;
  onThumbnailError?(theme: StoreThemeId): void;
  /** Shown above the choices, e.g. right after buying the store. */
  lead?: string | null;
}

export function StoreThemePickerView({
  businessName,
  current,
  selected,
  pending,
  error,
  onSelect,
  onApply,
  onClose,
  failedThumbnails,
  onThumbnailError,
  lead = null,
}: StoreThemePickerViewProps) {
  const formId = useId();
  const unchanged = selected === current;
  const applyHint = pending ? "Saving…" : unchanged ? "This is already your store's interior. Pick another theme to change it." : undefined;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!pending && !unchanged) onApply();
  };
  const footer = (
    <>
      {applyHint && !pending ? <span className="hint theme-apply-hint">{applyHint}</span> : null}
      <button type="button" className="btn" disabled={pending} onClick={onClose}>
        Cancel
      </button>
      <button type="submit" form={formId} className="btn btn-primary" disabled={pending || unchanged} title={applyHint} data-testid="store-theme-apply">
        {pending ? "Saving…" : `Use ${storeThemeLabel(selected) ?? selected}`}
      </button>
    </>
  );
  return (
    <Dialog title={`Store interior · ${businessName}`} onClose={() => !pending && onClose()} describedBy="store-theme-hint" wide footer={footer}>
      <form id={formId} className="stack" onSubmit={submit} data-testid="store-theme-picker">
        {lead ? (
          <p className="notice notice-success" style={{ margin: 0 }} data-testid="store-theme-lead">
            {lead}
          </p>
        ) : null}
        <p id="store-theme-hint" className="hint" style={{ margin: 0 }} data-testid="store-theme-intro">
          {STORE_THEME_PICKER_INTRO} Everyone inside your store sees the new floor, walls and fixtures right away, tinted with your brand colors. Your
          sign and store board stay as they are.
        </p>
        <fieldset className="choice-group theme-options" disabled={pending} aria-busy={pending}>
          <legend>
            Interior preset ({STORE_THEME_OPTIONS.length}): <span className="muted">{storeThemeLabel(current) ?? current} (current)</span>
          </legend>
          <div className="theme-option-list">
            {STORE_THEME_OPTIONS.map((opt) => {
              const descId = `${formId}-${opt.id}-desc`;
              const nameId = `${formId}-${opt.id}-name`;
              const currentId = `${formId}-${opt.id}-current`;
              return (
                <label key={opt.id} className="theme-option" data-current={opt.id === current ? "true" : undefined} data-testid={`store-theme-option-${opt.id}`}>
                  <StoreThemeThumbnailView themeId={opt.id} failed={failedThumbnails?.has(opt.id) ?? false} onError={() => onThumbnailError?.(opt.id)} />
                  <span className="theme-option-row">
                    <input
                      type="radio"
                      name="storeTheme"
                      value={opt.id}
                      checked={selected === opt.id}
                      onChange={() => onSelect(opt.id)}
                      aria-labelledby={opt.id === current ? `${nameId} ${currentId}` : nameId}
                      aria-describedby={descId}
                      data-testid={`store-theme-${opt.id}`}
                    />
                    <span className="theme-option-text">
                      <strong id={nameId}>{opt.label}</strong>
                      {opt.id === current ? (
                        <span id={currentId} className="badge">
                          Current
                        </span>
                      ) : null}
                      <span id={descId} className="small muted">
                        {opt.description}
                      </span>
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
        {error ? (
          <div className="notice notice-danger" role="alert" data-testid="store-theme-error">
            {error}
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}

/** Stateful picker: sends the choice and reports the server's answer. */
export function StoreThemeDialog({
  propertyId,
  businessName,
  current,
  onClose,
  onSaved,
  lead = null,
}: {
  propertyId: string;
  businessName: string;
  current: StoreThemeId;
  onClose(): void;
  onSaved(res: InteriorThemeUpdateResponse): void;
  lead?: string | null;
}) {
  const [selected, setSelected] = useState<StoreThemeId>(current);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState<ReadonlySet<StoreThemeId>>(() => new Set());
  const apply = async () => {
    if (pending || selected === current) return;
    setPending(true);
    setError(null);
    try {
      const res = await putInteriorTheme(propertyId, { theme: selected });
      onSaved(res);
    } catch (err) {
      setError(themeSaveErrorMessage(err));
    } finally {
      setPending(false);
    }
  };
  return (
    <StoreThemePickerView
      businessName={businessName}
      current={current}
      selected={selected}
      pending={pending}
      error={error}
      lead={lead}
      failedThumbnails={failed}
      onThumbnailError={(id) => setFailed((prev) => (prev.has(id) ? prev : new Set([...prev, id])))}
      onSelect={(t) => {
        setSelected(t);
        setError(null);
      }}
      onApply={() => void apply()}
      onClose={onClose}
    />
  );
}
